import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { FiRefreshCw } from 'react-icons/fi';
import { useGetRegistrationAnalysisResult } from '../../../../services/hooks/Registration/useGetRegistrationAnalysisResult';
import { useRegistrationDocumentStatus } from '../../../../services/hooks/Registration/useVacancyDocuments';
import { badgeColor } from '../../../../utils/badgeFunction';

interface RegistrationAnalysisResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  registrationId: string;
}

const RegistrationAnalysisResultModal: React.FC<RegistrationAnalysisResultModalProps> = ({
  isOpen,
  onClose,
  registrationId,
}) => {
  const query = useGetRegistrationAnalysisResult(registrationId, isOpen);
  const data = query.data;
  const registrationStatusColors = badgeColor(
    data?.registration_rejected ? 'indeferido' : data?.registration_status || '',
  );
  const filesQuery = useRegistrationDocumentStatus(registrationId, Boolean(isOpen && data?.documents.length));

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside" size="3xl">
      <ModalOverlay />
      <ModalContent maxW={['95%', '95%', '750px', '800px']} h="auto" maxH="calc(100dvh - 24px)" mx={3} my={3}>
        <ModalHeader borderBottomWidth="1px" borderColor="gray.200" px={[3, 3, 5]} py={3} pr={10} flexShrink={0}>
          <Text fontSize="16px" fontWeight="bold" textTransform="uppercase" lineHeight="1.3">
            Detalhes da análise da inscrição
          </Text>
        </ModalHeader>
        <ModalCloseButton aria-label="Fechar detalhes da análise" size="sm" top={2} right={2} />
        <ModalBody py={3} px={[3, 3, 5]} minH={0} overflowY="auto">
          {query.isLoading ? (
            <Stack align="center" justify="center" minH="180px" role="status">
              <Spinner size="lg" />
              <Text fontSize="sm">Carregando resultado da análise...</Text>
            </Stack>
          ) : query.isError ? (
            <Stack spacing={4} align="center" py={6}>
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  Não foi possível carregar o resultado da análise. Tente novamente.
                </AlertDescription>
              </Alert>
              <Button
                size="sm"
                colorScheme="blue"
                variant="outline"
                leftIcon={<FiRefreshCw />}
                onClick={() => void query.refetch()}
              >
                Tentar novamente
              </Button>
            </Stack>
          ) : data ? (
            <Stack spacing={3} minW={0}>
              <Stack spacing={3}>
                <Flex align="center" gap={2} wrap="wrap">
                  <Text fontSize="13px" fontWeight="bold">
                    Situação:
                  </Text>
                  <Badge
                    borderRadius={4}
                    fontSize="12px"
                    lineHeight="1.2"
                    px={1.5}
                    py="2px"
                    whiteSpace="normal"
                    aria-label={`Situação da inscrição: ${data.registration_status}`}
                    bg={registrationStatusColors?.bgColor || 'gray.100'}
                    color={registrationStatusColors?.color || 'gray.700'}
                  >
                    {data.registration_status}
                  </Badge>
                </Flex>
                {(data.registration_rejected || data.registration_rejection_reason) && (
                  <Alert status="error" borderRadius="md" alignItems="start" px={3} py={2}>
                    <AlertIcon />
                    <AlertDescription
                      display="flex"
                      flexDirection="column"
                      gap="5px"
                      fontSize="xs"
                      lineHeight="1.25"
                      color="gray.800"
                      whiteSpace="pre-wrap"
                      overflowWrap="anywhere"
                    >
                      <strong>Motivo do indeferimento:</strong>
                      <span>{data.registration_rejection_reason || 'Não informado.'}</span>
                    </AlertDescription>
                  </Alert>
                )}
              </Stack>
              <Divider />
              <Stack spacing={2}>
                <Flex justify="space-between" align="center" gap={2}>
                  <Text fontSize="13px" fontWeight="bold" textTransform="uppercase">
                    Documentos enviados
                  </Text>
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    color={data.rejected_documents_count > 0 ? 'red.600' : 'gray.600'}
                    whiteSpace="nowrap"
                    flexShrink={0}
                  >
                    Indeferidos: {data.rejected_documents_count}
                  </Text>
                </Flex>
                {filesQuery.isError && (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription fontSize="sm">
                      Não foi possível carregar os links dos arquivos.{' '}
                      <Button size="sm" variant="link" colorScheme="blue" onClick={() => void filesQuery.refetch()}>
                        Tentar novamente
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}
                {data.documents.length === 0 ? (
                  <Text fontSize="sm" color="gray.600">
                    Nenhum documento disponível para consulta.
                  </Text>
                ) : (
                  <Stack spacing={2}>
                    {data.documents.map((document) => (
                      <Box
                        key={document.id}
                        borderWidth="1px"
                        borderColor="gray.200"
                        borderRadius="md"
                        px={3}
                        py={2}
                        minW={0}
                      >
                        <Flex align="center" gap={2}>
                          <Box flex="1" minW={0}>
                            <Text fontSize="13px" fontWeight="semibold" overflowWrap="anywhere">
                              {document.label || document.type}
                            </Text>
                            {document.is_rejected && (
                              <Text fontSize="xs" color="gray.800" whiteSpace="pre-wrap" overflowWrap="anywhere" mt={1}>
                                <strong>Motivo do indeferimento: </strong>
                                {document.rejection_reason || 'Não informado.'}
                              </Text>
                            )}
                          </Box>
                          <Badge
                            borderRadius={4}
                            px={1.5}
                            py={0.5}
                            whiteSpace="normal"
                            flexShrink={0}
                            maxW="40%"
                            bg={badgeColor(document.status)?.bgColor || 'gray.100'}
                            color={badgeColor(document.status)?.color || 'gray.700'}
                          >
                            {document.status}
                          </Badge>
                        </Flex>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Stack>
          ) : (
            <Text fontSize="sm" color="gray.600" py={6}>
              Nenhum resultado de análise disponível para esta inscrição.
            </Text>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default RegistrationAnalysisResultModal;
